import React, { useState, useEffect } from 'react';
import './DriverPortal.css';
import { authenticateDriver } from '../config/drivers';


const WEBHOOK_URL = 'https://norcalmobileoilchange.app.n8n.cloud/webhook/driver-complete-job';
const GET_JOBS_URL = 'https://norcalmobileoilchange.app.n8n.cloud/webhook/driver-get-jobs';

function DriverPortal() {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [driverName, setDriverName] = useState('');

  // Jobs state
  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState('all'); // 'all', 'today', or specific date

  // Form state
  const [formData, setFormData] = useState({
    technicianName: '',
    customerPhone: '',
    customerEmail: '',
    actualMileage: '',
    oilTypeUsed: '5W-30 Full Synthetic',
    oilQuantityQts: '5',
    filterUsed: '',
    serviceNotes: '',
    totalCharged: '',
    paymentMethod: 'Zelle',
    phoneValidated: false,
    paymentReceived: false
  });

  // Extra services added during job
  const [extraServices, setExtraServices] = useState({
    cabinFilter: false,
    engineFilter: false,
    tireRotation: false,
    wiperBlades: false
  });

  const ADDON_PRICES = {
    cabinFilter: 35,
    engineFilter: 35,
    tireRotation: 35,
    wiperBlades: 35
  };

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');


  // Check for saved session
  useEffect(() => {
    const savedDriver = localStorage.getItem('driverName');
    const savedAuth = localStorage.getItem('driverAuth');
    const savedUsername = localStorage.getItem('driverUsername');
    if (savedDriver && savedAuth === 'true' && savedUsername) {
      setDriverName(savedDriver);
      setUsername(savedUsername);
      setIsAuthenticated(true);
    }
  }, []);

  // Handle username/password login
  const handleLogin = (e) => {
    e.preventDefault();
    const driver = authenticateDriver(username, password);
    if (driver) {
      setIsAuthenticated(true);
      setDriverName(driver.name);
      localStorage.setItem('driverAuth', 'true');
      localStorage.setItem('driverName', driver.name);
      localStorage.setItem('driverUsername', driver.username);
      setLoginError('');
      loadJobs();
    } else {
      setLoginError('Invalid username or password');
    }
  };

  // Logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('driverAuth');
    localStorage.removeItem('driverName');
    localStorage.removeItem('driverUsername');
    setUsername('');
    setPassword('');
    setDriverName('');
    setSelectedJob(null);
    setJobs([]);
  };

  // Dismiss a job (save to server Redis)
  const addDismissedJob = async (jobId) => {
    try {
      await fetch('/api/business?type=dismissed', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${driverName}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobId })
      });
    } catch (err) {
      console.error('Failed to save dismissed job:', err);
    }
  };

  // Load all jobs from Zoho via n8n webhook
  const loadJobs = async () => {
    setLoading(true);
    try {
      // Fetch dismissed jobs from server
      let dismissed = [];
      try {
        const dRes = await fetch('/api/business?type=dismissed', {
          headers: { 'Authorization': `Bearer ${driverName || 'NorCal'}` }
        });
        if (dRes.ok) {
          const dData = await dRes.json();
          dismissed = dData.dismissed || [];
        }
      } catch {}

      const response = await fetch(GET_JOBS_URL);
      const data = await response.json();

      // Transform Zoho data to our format - get ALL not completed jobs
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const transformedJobs = (data.data || [])
        .filter(job => {
          if (job.Job_Status === 'Completed') return false;
          // Remove jobs older than 1 week
          if (job.Scheduled_Date) {
            const jobDate = new Date(job.Scheduled_Date + 'T23:59:59');
            if (jobDate < oneWeekAgo) return false;
          }
          // Skip dismissed/cancelled jobs
          if (dismissed.includes(job.id)) return false;
          return true;
        })
        .sort((a, b) => {
          // Sort by date, then by time
          if (a.Scheduled_Date !== b.Scheduled_Date) {
            return a.Scheduled_Date.localeCompare(b.Scheduled_Date);
          }
          return (a.Time_Window || '').localeCompare(b.Time_Window || '');
        })
        .map(job => {
          // Parse vehicle info from Name field (format: "Job - FirstName LastName - Year Make Model")
          const nameParts = (job.Name || '').split(' - ');
          let vehicleInfo = { year: '', make: '', model: '' };
          if (nameParts.length >= 3) {
            const vehiclePart = nameParts[2].trim().split(' ');
            vehicleInfo = {
              year: vehiclePart[0] || '',
              make: vehiclePart[1] || '',
              model: vehiclePart.slice(2).join(' ') || ''
            };
          }

          // Parse add-ons from Service_Notes (format: "Add-ons: Service1, Service2")
          let addOns = [];
          const serviceNotes = job.Service_Notes || '';
          if (serviceNotes.includes('Add-ons:')) {
            const addOnsStr = serviceNotes.replace('Add-ons:', '').trim();
            addOns = addOnsStr.split(',').map(s => s.trim()).filter(s => s);
          }

          return {
            id: job.id,
            customerName: job.Customer?.name || nameParts[1]?.trim() || 'Unknown',
            customerEmail: job.Email || '',
            customerPhone: job.Phone || job.Customer_Phone || '',
            vehicleYear: vehicleInfo.year,
            vehicleMake: vehicleInfo.make,
            vehicleModel: vehicleInfo.model,
            serviceAddress: job.Service_Address || job.City || 'Address not specified',
            scheduledTime: job.Time_Window || '9:00 AM',
            scheduledDate: job.Scheduled_Date || '',
            serviceType: job.Service_Type || 'Standard Oil Change',
            price: job.Price || getPriceByServiceType(job.Service_Type),
            status: job.Job_Status || 'New',
            jobNumber: job.Job_Number,
            addOns: addOns
          };
        });

      // Enrich jobs with phone/email from Redis bookings (match by date + time)
      try {
        const bookingsRes = await fetch('/api/bookings?full=true');
        if (bookingsRes.ok) {
          const bookingsData = await bookingsRes.json();
          const redisBookings = bookingsData.bookings || [];
          const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
          transformedJobs.forEach(job => {
            // Convert job date (2026-04-03) to booking format (April 3, 2026)
            if (job.scheduledDate) {
              const d = new Date(job.scheduledDate + 'T12:00:00');
              const jobDateFmt = `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
              const jobTime = (job.scheduledTime || '').toLowerCase().replace(/\s+/g, ' ').trim();

              const match = redisBookings.find(b => {
                if (!b.phone) return false;
                if (b.date !== jobDateFmt) return false;
                // Also check time if available
                if (jobTime && b.time) {
                  return b.time.toLowerCase().replace(/\s+/g, ' ').trim() === jobTime;
                }
                return true;
              });
              if (match) {
                if (!job.customerPhone) job.customerPhone = match.phone;
                if (!job.customerEmail && match.email) job.customerEmail = match.email;
              }
            }
          });
        }
      } catch {}

      setAllJobs(transformedJobs);
      filterJobs(transformedJobs, selectedDate);
    } catch (error) {
      console.error('Error loading jobs:', error);
      setAllJobs([]);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter jobs by selected date
  const filterJobs = (jobsList, dateFilter) => {
    const today = new Date().toISOString().split('T')[0];

    if (dateFilter === 'all') {
      setJobs(jobsList);
    } else if (dateFilter === 'today') {
      setJobs(jobsList.filter(job => job.scheduledDate === today));
    } else {
      setJobs(jobsList.filter(job => job.scheduledDate === dateFilter));
    }
  };

  // Handle date filter change
  const handleDateFilter = (newDate) => {
    setSelectedDate(newDate);
    filterJobs(allJobs, newDate);
  };

  // Get unique dates from jobs for the filter
  const getUniqueDates = () => {
    const dates = [...new Set(allJobs.map(job => job.scheduledDate))].sort();
    return dates;
  };

  // Format date for display
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (dateStr === today.toISOString().split('T')[0]) return 'Today';
    if (dateStr === tomorrow.toISOString().split('T')[0]) return 'Tomorrow';

    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Select a job
  const handleSelectJob = (job) => {
    setSelectedJob(job);
    setFormData({
      ...formData,
      technicianName: driverName,
      customerPhone: job.customerPhone || '',
      customerEmail: job.customerEmail || '',
      totalCharged: (job.price || 125).toString(),
      filterUsed: getDefaultFilter(job.vehicleMake)
    });
    setSubmitSuccess(false);
    setSubmitError('');
  };

  // Get default filter based on vehicle make
  const getDefaultFilter = (make) => {
    const filters = {
      'Toyota': 'Toyota 04152-YZZA1',
      'Honda': 'Honda 15400-PLM-A02',
      'BMW': 'Mann HU816x',
      'Mercedes': 'Mann HU718/5x',
      'Ford': 'Motorcraft FL-820-S',
      'Chevrolet': 'AC Delco PF63E',
      'Nissan': 'Nissan 15208-65F0E'
    };
    return filters[make] || 'OEM Filter';
  };

  // Get price based on service type
  const getPriceByServiceType = (serviceType) => {
    const prices = {
      'Standard Oil Change': 99,
      'Standard Oil Change ($99)': 99,
      'European Oil Change': 125,
      'Light-Duty Trucks Oil Change': 125,
      'Diesel/Truck Oil Change ($209)': 209,
      'RV/Motorhome Oil Change': 250
    };
    return prices[serviceType] || 125;
  };

  const BASE_OIL_QTS = 5;
  const EXTRA_QT_PRICE = 5;

  // Round quarts: <.5 down, >=.5 up
  const roundQts = (qty) => Math.round(parseFloat(qty) || 0);

  // Calculate extra quart charge
  const extraQtCharge = (qty) => Math.max(0, roundQts(qty) - BASE_OIL_QTS) * EXTRA_QT_PRICE;

  // Recalculate total from base price + addons + extra quarts
  const recalcTotal = (qts, services) => {
    const basePrice = parseFloat(selectedJob?.price) || 99;
    const addonsTotal = Object.entries(services)
      .filter(([, on]) => on)
      .reduce((sum, [k]) => sum + ADDON_PRICES[k], 0);
    return String(basePrice + addonsTotal + extraQtCharge(qts));
  };

  // Handle form changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    if (name === 'oilQuantityQts') {
      setFormData(prev => ({
        ...prev,
        oilQuantityQts: value,
        totalCharged: recalcTotal(value, extraServices)
      }));
    } else {
      setFormData({
        ...formData,
        [name]: newValue
      });
    }
  };

  const toggleExtraService = (key) => {
    const updated = { ...extraServices, [key]: !extraServices[key] };
    setExtraServices(updated);
    setFormData(prev => ({ ...prev, totalCharged: recalcTotal(prev.oilQuantityQts, updated) }));
  };

  // Submit completed service
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.actualMileage) {
      setSubmitError('Please enter the vehicle mileage');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    const payload = {
      job_id: selectedJob.id,
      driver_name: formData.technicianName || driverName,
      actual_mileage: parseInt(formData.actualMileage),
      oil_type_used: formData.oilTypeUsed,
      oil_quantity_qts: parseFloat(formData.oilQuantityQts),
      filter_used: formData.filterUsed,
      service_notes: formData.serviceNotes,
      total_charged: parseFloat(formData.totalCharged),
      payment_method: formData.paymentMethod,
      payment_received: formData.paymentReceived,
      customer_email: selectedJob.customerEmail,
      customer_name: selectedJob.customerName,
      customer_phone: formData.customerPhone || selectedJob.customerPhone,
      vehicle_year: selectedJob.vehicleYear,
      vehicle_make: selectedJob.vehicleMake,
      vehicle_model: selectedJob.vehicleModel,
      service_address: selectedJob.serviceAddress
    };

    try {
      const bizHeaders = { 'Authorization': `Bearer ${driverName}`, 'Content-Type': 'application/json' };
      const vehicle = `${selectedJob.vehicleYear} ${selectedJob.vehicleMake} ${selectedJob.vehicleModel}`.trim();

      // Save client to business tracker
      const clientRes = await fetch('/api/business?type=clients', {
        method: 'POST', headers: bizHeaders,
        body: JSON.stringify({
          name: selectedJob.customerName,
          phone: formData.customerPhone || selectedJob.customerPhone || '—',
          email: formData.customerEmail || selectedJob.customerEmail || '—',
          address: selectedJob.serviceAddress,
          vehicle: vehicle,
          preferred_oil: formData.oilTypeUsed,
          technician: driverName
        })
      });
      if (!clientRes.ok) throw new Error('Failed to save client');
      const clientData = await clientRes.json();
      const clientId = clientData.client?.id;

      // Save order to business tracker
      const oilCost = roundQts(formData.oilQuantityQts) * 5;
      const totalCharged = parseFloat(formData.totalCharged) || 0;

      // Calculate extra service costs
      const engineFilterPrice = extraServices.engineFilter ? ADDON_PRICES.engineFilter : 0;
      const cabinFilterPrice = extraServices.cabinFilter ? ADDON_PRICES.cabinFilter : 0;
      const tireRotationPrice = extraServices.tireRotation ? ADDON_PRICES.tireRotation : 0;
      const wiperBladesPrice = extraServices.wiperBlades ? ADDON_PRICES.wiperBlades : 0;

      // Build extra services list
      const extraServicesList = [
        ...(selectedJob.addOns || []),
        ...(extraServices.cabinFilter ? ['Cabin Air Filter'] : []),
        ...(extraServices.engineFilter ? ['Engine Air Filter'] : []),
        ...(extraServices.tireRotation ? ['Tire Rotation'] : []),
        ...(extraServices.wiperBlades ? ['Wiper Blades'] : [])
      ];

      const engineFilterCost = extraServices.engineFilter ? 12 : 0;
      const cabinFilterCost = extraServices.cabinFilter ? 10 : 0;
      const totalCost = oilCost + 8 + 6 + 50 + engineFilterCost + cabinFilterCost; // oil + filter + gas + driver + extras
      const orderRes = await fetch('/api/business?type=orders', {
        method: 'POST', headers: bizHeaders,
        body: JSON.stringify({
          date: new Date().toISOString().split('T')[0],
          client_id: clientId,
          client_name: selectedJob.customerName,
          vehicle: vehicle,
          oil_change_price: totalCharged - engineFilterPrice - cabinFilterPrice - tireRotationPrice - wiperBladesPrice,
          oil_filter_price: 0,
          engine_filter_price: engineFilterPrice,
          cabin_filter_price: cabinFilterPrice,
          extra_services: extraServicesList.length > 0 ? extraServicesList.join(', ') : '—',
          total_revenue: totalCharged,
          oil_capacity_qt: roundQts(formData.oilQuantityQts),
          oil_cost: oilCost,
          filter_cost: 8, engine_filter_cost: engineFilterCost, cabin_filter_cost: cabinFilterCost,
          gas_cost: 6, driver_income: 50,
          total_cost: totalCost,
          technician: formData.technicianName || driverName,
          payment_method: formData.paymentMethod
        })
      });
      if (!orderRes.ok) throw new Error('Failed to save order');

      console.log('Service completed — saved to business tracker');
      setSubmitSuccess(true);

      // Also try n8n webhook (non-blocking, for Zoho sync)
      fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(() => {});

      // Remove completed job from list and persist
      addDismissedJob(selectedJob.id);
      setJobs(jobs.filter(j => j.id !== selectedJob.id));

      // Reset form after 3 seconds
      setTimeout(() => {
        setSelectedJob(null);
        setSubmitSuccess(false);
        setFormData({
          technicianName: '',
          customerPhone: '',
          customerEmail: '',
          actualMileage: '',
          oilTypeUsed: '5W-30 Full Synthetic',
          oilQuantityQts: '5',
          filterUsed: '',
          serviceNotes: '',
          totalCharged: '',
          paymentMethod: 'Zelle',
          phoneValidated: false,
          paymentReceived: false
        });
        setExtraServices({ cabinFilter: false, engineFilter: false, tireRotation: false, wiperBlades: false });
      }, 3000);

    } catch (error) {
      console.error('Error submitting service:', error);
      setSubmitError('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cancel a job (requires password)
  const handleCancelJob = async (job) => {
    const pwd = window.prompt(`Enter password to cancel job for ${job.customerName}:`);
    if (!pwd) return;
    if (pwd !== 'Test@123') {
      alert('Wrong password');
      return;
    }

    try {
      // Find matching booking in Redis by fetching all bookings
      const bookingsRes = await fetch('/api/bookings');
      if (bookingsRes.ok) {
        const data = await bookingsRes.json();
        const bookings = data.bookings || [];

        // Match by date and time
        const formatDate = (dateStr) => {
          if (!dateStr) return '';
          const d = new Date(dateStr + 'T12:00:00');
          const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
          return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
        };

        const jobDateFormatted = formatDate(job.scheduledDate);
        const jobTime = (job.scheduledTime || '').toLowerCase().replace(/\s+/g, ' ').trim();
        const matching = bookings.find(b => {
          if (!b.confirmationNumber) return false;
          if (b.date !== jobDateFormatted) return false;
          // Match by time if available
          const bTime = (b.time || '').toLowerCase().replace(/\s+/g, ' ').trim();
          if (jobTime && bTime) return bTime === jobTime;
          return true; // fallback: match by date only
        });

        if (matching) {
          await fetch('/api/bookings', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ confirmationNumber: matching.confirmationNumber })
          });
        }
      }

      // Persist dismissal so it survives page refresh
      addDismissedJob(job.id);

      // Remove from job list
      const updated = jobs.filter(j => j.id !== job.id);
      setJobs(updated);
      setAllJobs(allJobs.filter(j => j.id !== job.id));
      if (selectedJob?.id === job.id) setSelectedJob(null);

    } catch (error) {
      console.error('Error cancelling job:', error);
      alert('Failed to cancel. Try again.');
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="driver-portal">
        <div className="driver-login">
          <div className="login-card">
            <div className="login-logo">
              <span className="logo-icon">🔧</span>
              <h1>Driver Portal</h1>
              <p>NorCal Mobile Oil Change</p>
            </div>

            <form onSubmit={handleLogin} className="login-form">
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  autoComplete="username"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  autoComplete="current-password"
                  required
                />
              </div>

              {loginError && <p className="error-message">{loginError}</p>}

              <button type="submit" className="login-btn">
                Login
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }


  // Success Screen
  if (submitSuccess) {
    return (
      <div className="driver-portal">
        <div className="success-screen">
          <div className="success-card">
            <div className="success-icon">✓</div>
            <h2>Service Completed!</h2>
            <p>Receipt sent to {selectedJob?.customerEmail}</p>
            <p className="success-note">Returning to job list...</p>
          </div>
        </div>
      </div>
    );
  }

  // Job Details & Complete Form
  if (selectedJob) {
    return (
      <div className="driver-portal">
        <div className="driver-header">
          <button className="back-btn" onClick={() => setSelectedJob(null)}>
            ← Back
          </button>
          <h2>Complete Service</h2>
          <button className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>

        <div className="job-details-card">
          <div className="customer-info">
            <h3>{selectedJob.customerName}</h3>
            <p className="vehicle-info">
              {selectedJob.vehicleYear} {selectedJob.vehicleMake} {selectedJob.vehicleModel}
            </p>
            <p className="address-info">{selectedJob.serviceAddress}</p>
            <p className="service-type-info">{selectedJob.serviceType} - ${selectedJob.price}</p>
            <div className="form-row" style={{ marginTop: '8px' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="customerPhone">Phone *</label>
                <input
                  type="tel"
                  id="customerPhone"
                  value={formData.customerPhone}
                  onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                  placeholder="Customer phone"
                  style={{ borderColor: formData.customerPhone ? '#4ade80' : '#f87171' }}
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="customerEmail">Email (optional)</label>
                <input
                  type="email"
                  id="customerEmail"
                  value={formData.customerEmail}
                  onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                  placeholder="Customer email"
                />
              </div>
            </div>
            {selectedJob.addOns && selectedJob.addOns.length > 0 && (
              <div className="addons-info">
                <strong>Additional Services:</strong>
                <ul className="addons-list">
                  {selectedJob.addOns.map((addon, idx) => (
                    <li key={idx}>{addon}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="complete-form">
          <div className="form-section">
            <h4>Service Details</h4>

            <div className="form-group">
              <label htmlFor="technicianName">Technician Name</label>
              <input
                type="text"
                id="technicianName"
                name="technicianName"
                value={formData.technicianName}
                onChange={handleChange}
                placeholder="Technician name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="actualMileage">Current Mileage *</label>
              <input
                type="number"
                id="actualMileage"
                name="actualMileage"
                value={formData.actualMileage}
                onChange={handleChange}
                placeholder="e.g. 45000"
                inputMode="numeric"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="oilTypeUsed">Oil Type</label>
              <select
                id="oilTypeUsed"
                name="oilTypeUsed"
                value={formData.oilTypeUsed}
                onChange={handleChange}
              >
                <option value="5W-20 Full Synthetic">5W-20 Full Synthetic</option>
                <option value="5W-30 Full Synthetic">5W-30 Full Synthetic</option>
                <option value="0W-20 Full Synthetic">0W-20 Full Synthetic</option>
                <option value="0W-40 Full Synthetic">0W-40 Full Synthetic</option>
                <option value="5W-40 Full Synthetic">5W-40 Full Synthetic</option>
                <option value="15W-40 Diesel">15W-40 Diesel</option>
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="oilQuantityQts">Oil Qty (qts)</label>
                <input
                  type="number"
                  id="oilQuantityQts"
                  name="oilQuantityQts"
                  value={formData.oilQuantityQts}
                  onChange={handleChange}
                  step="0.1"
                  min="1"
                  max="20"
                />
              </div>

              <div className="form-group">
                <label htmlFor="filterUsed">Filter</label>
                <input
                  type="text"
                  id="filterUsed"
                  name="filterUsed"
                  value={formData.filterUsed}
                  onChange={handleChange}
                  placeholder="Filter part #"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="serviceNotes">Notes (optional)</label>
              <textarea
                id="serviceNotes"
                name="serviceNotes"
                value={formData.serviceNotes}
                onChange={handleChange}
                placeholder="Any notes about the service..."
                rows="3"
              />
            </div>
          </div>

          <div className="form-section">
            <h4>Additional Services</h4>
            <div className="extra-services-grid">
              {[
                { key: 'cabinFilter', label: 'Cabin Air Filter', price: ADDON_PRICES.cabinFilter },
                { key: 'engineFilter', label: 'Engine Air Filter', price: ADDON_PRICES.engineFilter },
                { key: 'tireRotation', label: 'Tire Rotation', price: ADDON_PRICES.tireRotation },
                { key: 'wiperBlades', label: 'Wiper Blades', price: ADDON_PRICES.wiperBlades }
              ].map(({ key, label, price }) => (
                <label key={key} className={`extra-service-item${extraServices[key] ? ' active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={extraServices[key]}
                    onChange={() => toggleExtraService(key)}
                  />
                  <span className="extra-service-label">{label}</span>
                  <span className="extra-service-price">+${price}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-section">
            <h4>Payment</h4>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="totalCharged">Amount ($)</label>
                <input
                  type="number"
                  id="totalCharged"
                  name="totalCharged"
                  value={formData.totalCharged}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="paymentMethod">Method</label>
                <select
                  id="paymentMethod"
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                >
                  <option value="Zelle">Zelle</option>
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                  <option value="Venmo">Venmo</option>
                </select>
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="phoneValidated"
                  checked={formData.phoneValidated}
                  onChange={handleChange}
                />
                <span className="checkmark"></span>
                Phone Number Validated
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="paymentReceived"
                  checked={formData.paymentReceived}
                  onChange={handleChange}
                />
                <span className="checkmark"></span>
                Payment Received
              </label>
            </div>
          </div>

          {submitError && <p className="error-message">{submitError}</p>}

          <button
            type="submit"
            className="submit-btn"
            disabled={isSubmitting || !formData.paymentReceived || !formData.phoneValidated}
          >
            {isSubmitting ? 'Submitting...' : 'Complete Service & Send Receipt'}
          </button>

          <button
            type="button"
            className="cancel-job-btn"
            onClick={() => handleCancelJob(selectedJob)}
            disabled={isSubmitting}
          >
            Cancel Job
          </button>
        </form>
      </div>
    );
  }

  // Jobs List Screen
  return (
    <div className="driver-portal">
      <div className="driver-header">
        <div className="header-info">
          <h2>Jobs</h2>
          <p className="driver-name">Hi, {driverName}</p>
        </div>
        <div className="header-actions">
          <button className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      {/* Date Filter */}
      <div className="date-filter">
        <button
          className={`filter-btn ${selectedDate === 'all' ? 'active' : ''}`}
          onClick={() => handleDateFilter('all')}
        >
          All ({allJobs.length})
        </button>
        <button
          className={`filter-btn ${selectedDate === 'today' ? 'active' : ''}`}
          onClick={() => handleDateFilter('today')}
        >
          Today
        </button>
        {getUniqueDates().map(date => (
          date !== new Date().toISOString().split('T')[0] && (
            <button
              key={date}
              className={`filter-btn ${selectedDate === date ? 'active' : ''}`}
              onClick={() => handleDateFilter(date)}
            >
              {formatDateDisplay(date)}
            </button>
          )
        ))}
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading jobs...</p>
        </div>
      ) : jobs.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📋</span>
          <h3>No Jobs</h3>
          <p>{selectedDate === 'all' ? 'No pending jobs' : 'No jobs for selected date'}</p>
          <button className="refresh-btn" onClick={loadJobs}>
            Refresh
          </button>
        </div>
      ) : (
        <div className="jobs-list">
          {jobs.map((job) => (
            <div
              key={job.id}
              className="job-card"
              onClick={() => handleSelectJob(job)}
            >
              <div className="job-time">
                <span className="job-date-label">{formatDateDisplay(job.scheduledDate)}</span>
                <span>{job.scheduledTime}</span>
              </div>
              <div className="job-info">
                <h3>{job.customerName}</h3>
                <p className="job-vehicle">
                  {job.vehicleYear} {job.vehicleMake} {job.vehicleModel}
                </p>
                <p className="job-address">{job.serviceAddress}</p>
              </div>
              <div className="job-meta">
                <span className="job-type">{job.serviceType}</span>
                <span className="job-price">${job.price}</span>
                {job.addOns && job.addOns.length > 0 && (
                  <div className="job-addons">
                    +{job.addOns.length} add-on{job.addOns.length > 1 ? 's' : ''}
                  </div>
                )}
              </div>
              <div className="job-arrow">→</div>
            </div>
          ))}
        </div>
      )}

      <button className="refresh-fab" onClick={loadJobs}>
        ↻
      </button>
    </div>
  );
}

export default DriverPortal;
